import React, { useEffect, useState } from "react";
import {
  Panel,
  PanelType,
  Spinner,
  SpinnerSize,
  PrimaryButton,
  DefaultButton,
  TextField,
  IconButton,
  MessageBar,
  MessageBarType,
} from "@fluentui/react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { Tag, listFileTags, addFileTag, deleteFileTag } from "@/api/files";

interface TagsPanelProps {
  fileId: number | undefined;
  isOpen: boolean;
  onDismiss: () => void;
}

interface TagFormValues {
  name: string;
  description: string;
}

const validationSchema = Yup.object({
  name: Yup.string().required("Tag name is required").trim(),
  description: Yup.string().required("Description is required").trim(),
});

const TagsPanel: React.FC<TagsPanelProps> = ({ fileId, isOpen, onDismiss }) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isAddingTag, setIsAddingTag] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [deletingTagId, setDeletingTagId] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen && fileId !== undefined) {
      setLoading(true);
      listFileTags(fileId, { pageNumber: 1, pageSize: 100 })
        .then((response) => {
          setTags(response?.result || []);
        })
        .catch((error) => {
          console.error("Failed to load tags:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (isOpen && fileId === undefined) {
      setTags([]);
      setLoading(false);
    }
  }, [fileId, isOpen]);

  const loadTags = async () => {
    if (fileId !== undefined) {
      setLoading(true);
      try {
        const response = await listFileTags(fileId, {
          pageNumber: 1,
          pageSize: 100,
        });
        setTags(response?.result || []);
      } catch (error) {
        console.error("Failed to load tags:", error);
        setErrorMessage("Failed to load tags");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleAddTag = async (values: TagFormValues, { resetForm }: { resetForm: () => void }) => {
    if (!fileId) {
      setErrorMessage("File ID is required");
      return;
    }

    setIsAddingTag(true);
    setErrorMessage("");

    try {
      await addFileTag(fileId, values.name.trim(), values.description.trim());
      resetForm();
      setShowAddForm(false); // Hide form after successful add
      await loadTags(); // Reload tags after adding
    } catch (error) {
      console.error("Failed to add tag:", error);
      setErrorMessage("Failed to add tag");
    } finally {
      setIsAddingTag(false);
    }
  };

  const handleDeleteTag = async (tagId: number) => {
    if (!fileId) return;

    setDeletingTagId(tagId);
    setErrorMessage("");

    try {
      await deleteFileTag(fileId, tagId);
      await loadTags(); // Reload tags after deleting
    } catch (error) {
      console.error("Failed to delete tag:", error);
      setErrorMessage("Failed to delete tag");
    } finally {
      setDeletingTagId(null);
    }
  };

  const handleCancelAddTag = () => {
    setErrorMessage("");
    setShowAddForm(false);
  };

  const handleShowAddForm = () => {
    setShowAddForm(true);
    setErrorMessage("");
  };

  return (
    <Panel
      headerText="File Tags"
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.medium}
      closeButtonAriaLabel="Close tags panel"
      isLightDismiss={true}
    >
      <div style={{ padding: "20px" }}>
        {errorMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setErrorMessage("")}
            style={{ marginBottom: "16px" }}
          >
            {errorMessage}
          </MessageBar>
        )}

        {/* Add Tag Section */}
        {showAddForm && (
          <div
            style={{
              marginBottom: "24px",
              padding: "16px",
              backgroundColor: "#f8f9fa",
              borderRadius: "4px",
            }}
          >
            <h4 style={{ margin: "0 0 12px 0", color: "#323130" }}>
              Add New Tag
            </h4>
            <Formik
              initialValues={{ name: "", description: "" }}
              validationSchema={validationSchema}
              onSubmit={handleAddTag}
            >
              {({ values, errors, touched, handleChange, handleBlur, isValid, dirty }) => (
                <Form>
                  <Field name="name">
                    {() => (
                      <TextField
                        label="Name"
                        name="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter tag name"
                        required
                        errorMessage={touched.name && errors.name ? errors.name : undefined}
                        style={{ marginBottom: "8px" }}
                      />
                    )}
                  </Field>
                  <Field name="description">
                    {() => (
                      <TextField
                        label="Description"
                        name="description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="Enter tag description"
                        required
                        multiline
                        rows={2}
                        errorMessage={touched.description && errors.description ? errors.description : undefined}
                        style={{ marginBottom: "32px" }}
                      />
                    )}
                  </Field>
                  <div style={{ display: "flex", gap: "8px", marginTop: "16px" }}>
                    <PrimaryButton
                      text="Add Tag"
                      type="submit"
                      disabled={isAddingTag || !isValid || !dirty || !fileId}
                      styles={{
                        root: {
                          minHeight: "40px",
                          padding: "8px 20px",
                        },
                        label: {
                          padding: "0 4px",
                        },
                      }}
                    />
                    <DefaultButton
                      text="Cancel"
                      onClick={handleCancelAddTag}
                      disabled={isAddingTag}
                      styles={{
                        root: {
                          minHeight: "40px",
                          padding: "8px 20px",
                        },
                        label: {
                          padding: "0 4px",
                        },
                      }}
                    />
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}

        {/* Tags List */}
        <div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            <h4 style={{ margin: "0", color: "#323130" }}>Tags</h4>
            {!showAddForm && (
              <IconButton
                iconProps={{ iconName: "Add" }}
                title="Add new tag"
                ariaLabel="Add new tag"
                onClick={handleShowAddForm}
                disabled={!fileId}
                styles={{
                  root: {
                    color: "#0078d4",
                  },
                  rootHovered: {
                    backgroundColor: "#f3f2f1",
                  },
                }}
              />
            )}
          </div>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100px",
              }}
            >
              <Spinner size={SpinnerSize.medium} label="Loading tags..." />
            </div>
          ) : tags.length > 0 ? (
            <div>
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  style={{
                    padding: "16px",
                    border: "1px solid #edebe9",
                    borderRadius: "4px",
                    marginBottom: "12px",
                    backgroundColor: "#ffffff",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: "600",
                        color: "#323130",
                        marginBottom: "4px",
                        fontSize: "16px",
                      }}
                    >
                      {tag.name || "Unnamed Tag"}
                    </div>
                    <div
                      style={{
                        color: "#605e5c",
                        fontSize: "14px",
                        lineHeight: "1.4",
                      }}
                    >
                      {tag.description || "No description available"}
                    </div>
                  </div>
                  <IconButton
                    iconProps={{ iconName: "Delete" }}
                    title="Delete tag"
                    ariaLabel="Delete tag"
                    onClick={() => handleDeleteTag(tag.id)}
                    disabled={deletingTagId === tag.id}
                    styles={{
                      root: {
                        color: "#d13438",
                        marginLeft: "8px",
                      },
                      rootHovered: {
                        backgroundColor: "#fdf2f2",
                        color: "#a4262c",
                      },
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "40px 20px",
                color: "#605e5c",
                border: "1px dashed #c8c6c4",
                borderRadius: "4px",
              }}
            >
              <div style={{ fontSize: "16px", marginBottom: "8px" }}>
                No tags found
              </div>
              <div style={{ fontSize: "14px" }}>
                Click the + button above to add your first tag.
              </div>
            </div>
          )}
        </div>
      </div>
    </Panel>
  );
};

export default TagsPanel;
